// Remembered transcription options — the language, and the last vocabulary chosen per
// language. Persisted in localStorage, mirroring ../hooks/usePlaybackRate.ts exactly: small
// primitive values, never audio or transcript content, so client-side storage is fine, and
// every read is validated against a narrow allowed set so a corrupted or hand-edited value
// cannot select something nonsensical.
//
// Why remember at all: a user who works in Spanish picks Spanish and their Spanish vocabulary
// on every single recording. Defaulting to the last choice is the same reasoning as playback
// speed — the preference is stable per person, and re-picking it every time is pure friction.
//
// Deliberately NOT a server-side user preference: that would need a table, a migration, an API
// surface and cache invalidation to store something whose worst-case failure is "the dropdown
// starts on English". localStorage's tradeoff (per-browser, not per-account) is the right one
// here, and is the tradeoff already accepted for playback speed.
import { useCallback, useState } from 'react';
import type { EngineChoice, Vocabulary } from '@scribe/core';
import {
  DEFAULT_LANGUAGE,
  isTranscriptionLanguage,
  selectableVocabularies,
  type TranscriptionLanguage,
} from '../lib/languages';

const LANGUAGE_KEY = 'scribe.transcription-language.v1';
/** Vocabulary is remembered PER LANGUAGE. One shared key would hand a Spanish vocabulary to an
 *  English recording — the mismatch AWS rejects outright (plan-multilingual.md §1 Bug A) — so
 *  switching language must never carry the other language's vocabulary across. */
const VOCABULARY_KEY_PREFIX = 'scribe.transcription-vocabulary.v1.';

function readStoredLanguage(): TranscriptionLanguage {
  try {
    const raw = localStorage.getItem(LANGUAGE_KEY);
    return isTranscriptionLanguage(raw) ? raw : DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

function readStoredVocabulary(language: TranscriptionLanguage): string {
  try {
    return localStorage.getItem(VOCABULARY_KEY_PREFIX + language) ?? '';
  } catch {
    return '';
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Quota or serialization failure — non-fatal, exactly as in usePlaybackRate. The
    // in-memory choice keeps working for this session; it just won't survive a reload. Never
    // throw from storage.
  }
}

/**
 * The remembered language + vocabulary, and setters that persist as they go.
 *
 * Takes the raw vocabulary list and does the language filtering itself, returning
 * `selectable` for the caller to render. That ordering is deliberate: the selectable list
 * depends on the chosen language, and the effective vocabulary depends on the selectable list,
 * so a caller computing the filter would have to do it before the hook that decides the
 * language — a cycle. Owning both here also means the "discard a vocabulary that is no longer
 * offered" rule exists once rather than at all three call sites.
 *
 * A remembered vocabulary that has been deleted, unpublished, or belongs to the other language
 * resolves to "None", so a stale preference degrades instead of submitting a name the server
 * would reject. While the list is still loading the remembered value is preserved rather than
 * cleared, so it survives the fetch.
 */
export function useTranscriptionPrefs(vocabularies: readonly Vocabulary[] | undefined): {
  language: TranscriptionLanguage;
  setLanguage: (language: TranscriptionLanguage) => void;
  vocabularyName: string;
  setVocabularyName: (name: string) => void;
  selectable: Vocabulary[];
} {
  const [language, setLanguageState] = useState<TranscriptionLanguage>(readStoredLanguage);
  const [vocabularyName, setVocabularyState] = useState<string>(() =>
    readStoredVocabulary(readStoredLanguage()),
  );

  const setLanguage = useCallback((next: TranscriptionLanguage) => {
    setLanguageState(next);
    write(LANGUAGE_KEY, next);
    // Switch to that language's own remembered vocabulary rather than keeping the current
    // one — see VOCABULARY_KEY_PREFIX.
    setVocabularyState(readStoredVocabulary(next));
  }, []);

  const setVocabularyName = useCallback(
    (name: string) => {
      setVocabularyState(name);
      write(VOCABULARY_KEY_PREFIX + language, name);
    },
    [language],
  );

  const selectable = selectableVocabularies(vocabularies, language);

  // A remembered name that is no longer on offer resolves to "None". Computed rather than
  // written back to storage: the vocabulary may simply not have loaded yet, and clearing the
  // preference on a transient empty list would lose it permanently.
  const stillOffered = selectable.some((v) => v.name === vocabularyName);
  const effectiveVocabularyName = vocabularyName && stillOffered ? vocabularyName : '';

  return {
    language,
    setLanguage,
    vocabularyName: effectiveVocabularyName,
    setVocabularyName,
    selectable,
  };
}

/** The remembered language, for a surface that needs it as a FALLBACK rather than as its
 *  primary default.
 *
 * RecordingDetail's re-transcribe is the case: the better default there is whatever language
 * that recording was last transcribed with, and this only fills in when its `job_spec` carries
 * none. That surface also deliberately does not write back — re-transcribing one old Spanish
 * recording should not switch the user's default for every new recording afterwards. */
export function storedTranscriptionLanguage(): TranscriptionLanguage {
  return readStoredLanguage();
}

/** Build the `job_spec` fragment for a chosen language + vocabulary.
 *
 * `language` is ALWAYS sent, even when it is the default. Omitting it would work — the server
 * treats a missing language as `en-US` — but then a persisted job_spec cannot distinguish "the
 * user chose English" from "the user chose nothing", which is exactly what
 * RecordingDetail needs in order to pre-select the language on a re-transcribe. Being explicit
 * also means a later change to the server default cannot retroactively alter what an existing
 * recording meant. */
export function buildJobSpec(
  language: TranscriptionLanguage,
  vocabularyName: string,
  combinedLanguageOptions: readonly string[],
  engine: EngineChoice = 'aws',
): Record<string, unknown> {
  return {
    language,
    ...(language === 'multi' ? { language_options: [...combinedLanguageOptions] } : {}),
    ...(vocabularyName ? { vocabulary_name: vocabularyName } : {}),
    // WHISPER-1: omitted for the default, so the AWS request shape is unchanged.
    ...(engine !== 'aws' ? { engine } : {}),
  };
}
